/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;
const libGeolocationCSS = require('./PictProvider-InputType-GeolocationSelector-CSS.js');

// Coordinate display precision (decimal degrees) for the combined read-only readout.
const GEO_DISPLAY_PRECISION = 6;
// Fixed key for the transient "edit location" modal instance (only one modal is open at a time).
const GEO_MODAL_KEY = 'GEO-MODAL';
// Wide "no fix yet" default view (continental US) used when opening a map with no value.
const GEO_DEFAULT_CENTER = { Latitude: 39.5, Longitude: -98.35 };

/*
	Geolocation input type (both `GeolocationSelector` and `GeolocationSelectorWithMap`).

	Single control, both single-record and tabular. The map variant adds a full-width Leaflet map with a
	draggable marker + click-to-place + address search; the no-map variant is the same control without the
	map. The value persists as a {Latitude, Longitude} object via two informary-bound number inputs whose
	data-i-datum is suffixed `.Latitude`/`.Longitude`.

	Maps are LAZY-mounted via an IntersectionObserver (mounted only once their container is actually
	visible, then invalidateSize()). This resolves the old "map mis-centers when initialized behind a form
	tab" bug the previous implementation punted on, and is the same trick that makes the modal-hosted map
	work.

	All transient state (address-search results, per-map data) is keyed by the field rawHTMLID, so a form
	may hold many geolocation fields, N tabular rows, and the edit modal at once without collisions.
*/
class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
		// Address-search results, keyed by rawHTMLID (fields + the GEO_MODAL_KEY modal instance).
		this._addressResultsByID = {};
		// Guards onDataChange from re-syncing the map when the change originated from a map gesture or a
		// programmatic commit (only user-typed coordinate edits should move the marker).
		this._suppressMapSync = {};
		// Pending lazy-mount IntersectionObservers, keyed by rawHTMLID, so we can disconnect on teardown.
		this._mapObservers = {};
		// Latest working value for the open modal (read on Save, robust to modal-DOM teardown timing).
		this._modalValue = { Latitude: '', Longitude: '' };
		// Guards the one-time CSS registration.
		this._cssInjected = false;
		// Row identity for full-size tabular/record-set cells, keyed by `<rawHTMLID>-<rowIndex>`. The
		// address search is written against a single instance id, so this is how a result picked in a
		// row finds its way back to the right row commit.
		this._rowContexts = {};
	}

	/** The instance id a tabular/record-set row shares with the address-search DOM helpers. */
	getRowKey(pInputHTMLID, pRowIndex)
	{
		return `${pInputHTMLID}-${pRowIndex}`;
	}

	/** Register the component's phone-layout rules once per app. */
	_injectCSS()
	{
		if (this._cssInjected || !this.pict || !this.pict.CSSMap || (typeof this.pict.CSSMap.addCSS !== 'function'))
		{
			return;
		}
		this._cssInjected = true;
		this.pict.CSSMap.addCSS('Headlight-GeolocationSelector', libGeolocationCSS, 500);
		if (typeof this.pict.CSSMap.injectCSS === 'function')
		{
			this.pict.CSSMap.injectCSS();
		}
	}

	// --- HTML id helpers -----------------------------------------------------------------------------

	getMapContainerHTMLID(pInputHTMLID)
	{
		return `#MAP-CONTAINER-FOR-${pInputHTMLID}`;
	}

	/** Strip the map container/element prefix back to the owning rawHTMLID (or modal key). */
	getMapIDFromContainerID(pContainerID)
	{
		if (!pContainerID)
		{
			return '';
		}
		if (pContainerID.indexOf('MAP-CONTAINER-FOR-') > -1)
		{
			return pContainerID.replace('MAP-CONTAINER-FOR-', '');
		}
		if (pContainerID.indexOf('MAP-FOR-') > -1)
		{
			return pContainerID.replace('MAP-FOR-', '');
		}
		return pContainerID;
	}

	getLatitudeInputHTMLID(pInputHTMLID)
	{
		return `#LATITUDE-FOR-${pInputHTMLID}`;
	}

	getLongitudeInputHTMLID(pInputHTMLID)
	{
		return `#LONGITUDE-FOR-${pInputHTMLID}`;
	}

	getCombinedDisplayID(pInputHTMLID)
	{
		return `#GEO-DISPLAY-FOR-${pInputHTMLID}`;
	}

	getAddressResultsID(pInputHTMLID)
	{
		return `GEO-ADDRESS-RESULTS-${pInputHTMLID}`;
	}

	getAddressInputID(pInputHTMLID)
	{
		return `GEO-ADDRESS-INPUT-${pInputHTMLID}`;
	}

	getTabularLatitudeInputID(pInputHTMLID, pRowIndex)
	{
		return `#GEO-LAT-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularLongitudeInputID(pInputHTMLID, pRowIndex)
	{
		return `#GEO-LONG-TABULAR-DATA-${pInputHTMLID}-${pRowIndex}`;
	}

	getTabularDisplayID(pInputHTMLID, pRowIndex)
	{
		return `#GEO-DISPLAY-TABULAR-${pInputHTMLID}-${pRowIndex}`;
	}

	/**
	 * The full-size row presentation's visible coordinate editors. These are NOT informary-bound —
	 * the hidden Begin-partial inputs remain the only binding — so they are kept in sync from the
	 * value and commit back through the row methods.
	 */
	getRowEditorLatitudeID(pInputHTMLID, pRowIndex)
	{
		return `#GEO-RS-LAT-${pInputHTMLID}-${pRowIndex}`;
	}

	getRowEditorLongitudeID(pInputHTMLID, pRowIndex)
	{
		return `#GEO-RS-LONG-${pInputHTMLID}-${pRowIndex}`;
	}

	// --- DOM value get/set (loosely typed — these are always <input> elements found by id/selector) ---

	/**
	 * @param {any} pElement
	 * @return {string}
	 */
	_domValue(pElement)
	{
		return (pElement && pElement.value != null) ? String(pElement.value) : '';
	}

	/**
	 * @param {any} pElement
	 * @param {any} pValue
	 */
	_setDomValue(pElement, pValue)
	{
		if (pElement)
		{
			pElement.value = (pValue === '' || pValue == null) ? '' : String(pValue);
		}
	}

	// --- Value coercion / formatting -----------------------------------------------------------------

	/**
	 * Normalize any stored representation to a { Latitude, Longitude } pair. Accepts the canonical object,
	 * a legacy JSON string ({"Latitude":..,"Longitude":..} — the old WithMap serialization), or empty.
	 * @param {any} pValue
	 * @return {{ Latitude: (string|number), Longitude: (string|number) }}
	 */
	_coerceLatLong(pValue)
	{
		if (pValue == null || pValue === '' || pValue === 'null')
		{
			return { Latitude: '', Longitude: '' };
		}
		if (typeof pValue === 'object')
		{
			return { Latitude: pValue.Latitude != null ? pValue.Latitude : '', Longitude: pValue.Longitude != null ? pValue.Longitude : '' };
		}
		if (typeof pValue === 'string')
		{
			try
			{
				const tmpParsed = JSON.parse(pValue);
				if (tmpParsed && typeof tmpParsed === 'object')
				{
					return { Latitude: tmpParsed.Latitude != null ? tmpParsed.Latitude : '', Longitude: tmpParsed.Longitude != null ? tmpParsed.Longitude : '' };
				}
			}
			catch (pError)
			{/* not JSON — fall through to empty */}
		}
		return { Latitude: '', Longitude: '' };
	}

	/** True when both coordinates are present, numeric, and within valid lat/long ranges. */
	_inRange(pLatitude, pLongitude)
	{
		if (pLatitude === '' || pLongitude === '' || pLatitude == null || pLongitude == null)
		{
			return false;
		}
		const tmpLat = Number(pLatitude);
		const tmpLng = Number(pLongitude);
		return Number.isFinite(tmpLat) && Number.isFinite(tmpLng) && (tmpLat >= -90) && (tmpLat <= 90) && (tmpLng >= -180) && (tmpLng <= 180);
	}

	/** Format the combined readout string, or the empty-state placeholder. */
	formatLocationDisplay(pLatitude, pLongitude)
	{
		if (!this._inRange(pLatitude, pLongitude))
		{
			return 'No location set';
		}
		return `${Number(pLatitude).toFixed(GEO_DISPLAY_PRECISION)}, ${Number(pLongitude).toFixed(GEO_DISPLAY_PRECISION)}`;
	}

	/** Repaint the single-record combined readout from the current lat/long inputs. No-op if absent. */
	refreshCombinedDisplay(pInputHTMLID)
	{
		if (!document.querySelector(this.getCombinedDisplayID(pInputHTMLID)))
		{
			return;
		}
		const tmpLat = $(this.getLatitudeInputHTMLID(pInputHTMLID)).val();
		const tmpLng = $(this.getLongitudeInputHTMLID(pInputHTMLID)).val();
		this.pict.ContentAssignment.assignContent(this.getCombinedDisplayID(pInputHTMLID), this.formatLocationDisplay(tmpLat, tmpLng));
	}

	/**
	 * Print/PDF only: populate the static map <img> from the value via the Google Static Maps helper. A
	 * single image (which the PDF renderer waits for) fills its container reliably, unlike a live Leaflet
	 * map whose raster tiles get sized to the narrower init width and leave the frame's right edge blank.
	 * No-op when the img is absent (Edit/Read themes render a live map instead) or the value is empty.
	 * @param {string} pInputHTMLID
	 * @param {{ Latitude: (string|number), Longitude: (string|number) }} pSeed
	 */
	_refreshStaticMapImage(pInputHTMLID, pSeed)
	{
		this._paintStaticMapImg(document.getElementById(`MAP-IMG-FOR-${pInputHTMLID}`), pSeed);
	}

	/** Build the Google Static Maps URL for a value (framed at neighborhood zoom), or '' when empty. */
	_staticMapURL(pSeed)
	{
		const tmpUtility = this.pict.providers.UtilityProvider;
		if (!pSeed || !this._inRange(pSeed.Latitude, pSeed.Longitude) || !tmpUtility || (typeof tmpUtility.getGoogleMapURLFromEntityRecord !== 'function'))
		{
			return '';
		}
		let tmpURL = tmpUtility.getGoogleMapURLFromEntityRecord(
			{ Latitude: pSeed.Latitude, Longitude: pSeed.Longitude },
			{ size: '640x360', scale: 2, markerColor: 'blue' });
		// The helper emits only a marker (no center/zoom → Google auto-fits a single point oddly); frame it
		// on the point at a neighborhood zoom to match the live map.
		if (tmpURL)
		{
			tmpURL += `&zoom=14&center=${encodeURIComponent(`${pSeed.Latitude},${pSeed.Longitude}`)}`;
		}
		return tmpURL;
	}

	/** Paint a static-map <img>: set src + show, or clear + hide when there's no URL. No-op if absent. */
	_paintStaticMapImg(pImgEl, pSeed)
	{
		if (!pImgEl)
		{
			return;
		}
		const tmpURL = this._staticMapURL(pSeed);
		if (tmpURL)
		{
			pImgEl.setAttribute('src', tmpURL);
			pImgEl.style.display = 'block';
		}
		else
		{
			pImgEl.removeAttribute('src');
			pImgEl.style.display = 'none';
		}
	}

	/** Print/PDF only: static map image for one record-set record (the Print theme's map host is an <img>). */
	_refreshRecordSetRowStaticImage(pInput, pRowIndex, pSeed)
	{
		this._paintStaticMapImg(document.getElementById(`GEO-RS-MAPIMG-FOR-${pInput.Macro.RawHTMLID}-${pRowIndex}`), pSeed);
	}

	_toast(pMessage, pType)
	{
		if (this.pict.views.HeadLightPanels && (typeof this.pict.views.HeadLightPanels.toast === 'function'))
		{
			this.pict.views.HeadLightPanels.toast({ message: pMessage, type: pType || 'is-info' });
		}
	}

	// --- Single-record lifecycle ---------------------------------------------------------------------

	onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this._injectCSS();
		const tmpHideMap = pInput.PictForm.InputType !== 'GeolocationSelectorWithMap';
		pView.options.hideLeafletMap = tmpHideMap;

		const rawHTMLID = pInput.Macro.RawHTMLID;
		// Bind the two number inputs to the .Latitude/.Longitude sub-addresses so the datum marshals as a
		// { Latitude, Longitude } object (the canonical shape for both variants).
		const tmpLat = $(this.getLatitudeInputHTMLID(rawHTMLID));
		const tmpLong = $(this.getLongitudeInputHTMLID(rawHTMLID));
		if (tmpLat.length && tmpLat.data('iDatum'))
		{
			tmpLat.attr('data-i-datum', `${tmpLat.data('iDatum')}.Latitude`);
		}
		if (tmpLong.length && tmpLong.data('iDatum'))
		{
			tmpLong.attr('data-i-datum', `${tmpLong.data('iDatum')}.Longitude`);
		}

		// Seed inputs from the incoming value (covers legacy JSON-string persistence, where informary can't
		// reach the .Latitude/.Longitude sub-addresses of a string base value).
		const tmpSeed = this._coerceLatLong(pValue);
		if (tmpSeed.Latitude !== '' && !tmpLat.val())
		{
			tmpLat.val(tmpSeed.Latitude);
		}
		if (tmpSeed.Longitude !== '' && !tmpLong.val())
		{
			tmpLong.val(tmpSeed.Longitude);
		}
		this.refreshCombinedDisplay(rawHTMLID);

		if (!tmpHideMap)
		{
			// Print theme renders a static map image; Edit/Read mount the live Leaflet map. Each no-ops when
			// its target element is absent, so exactly one runs per theme.
			this._refreshStaticMapImage(rawHTMLID, tmpSeed);
			this._observeAndMountMap(rawHTMLID, pInput);
		}
		return super.onInputInitialize(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshal a value into the single-record control. Coerces object/legacy-string, repaints the readout,
	 * and (map variant) moves the marker. Populating the inputs here also migrates legacy string data to the
	 * object shape on next save.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const rawHTMLID = pInput.Macro.RawHTMLID;
		const tmpSeed = this._coerceLatLong(pValue);
		const tmpLat = $(this.getLatitudeInputHTMLID(rawHTMLID));
		const tmpLong = $(this.getLongitudeInputHTMLID(rawHTMLID));
		if (tmpLat.length)
		{
			tmpLat.val(tmpSeed.Latitude === '' ? '' : tmpSeed.Latitude);
		}
		if (tmpLong.length)
		{
			tmpLong.val(tmpSeed.Longitude === '' ? '' : tmpSeed.Longitude);
		}
		this.refreshCombinedDisplay(rawHTMLID);
		this._refreshStaticMapImage(rawHTMLID, tmpSeed);
		// Marshal runs after every tabular row add/delete (marshalFormSections), so this is where a map
		// whose DOM was just regenerated gets re-mounted. No-op when the map is healthy or absent.
		this._observeAndMountMap(rawHTMLID, pInput);
		if (this._inRange(tmpSeed.Latitude, tmpSeed.Longitude))
		{
			this.moveMarkerTo(rawHTMLID, Number(tmpSeed.Latitude), Number(tmpSeed.Longitude));
		}
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		const rawHTMLID = pInput.Macro.RawHTMLID;
		this.refreshCombinedDisplay(rawHTMLID);
		// A user edit of the lat/long fields (not a map gesture / programmatic commit): follow it on the map.
		if (!this._suppressMapSync[rawHTMLID])
		{
			const tmpLat = $(this.getLatitudeInputHTMLID(rawHTMLID)).val();
			const tmpLng = $(this.getLongitudeInputHTMLID(rawHTMLID)).val();
			if (this._inRange(tmpLat, tmpLng))
			{
				this.moveMarkerTo(rawHTMLID, Number(tmpLat), Number(tmpLng));
			}
		}
		return super.onDataChange(pView, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	// --- Map: lazy mount, marker, click/drag/programmatic move ---------------------------------------

	/**
	 * Defer the Leaflet mount until the map container is actually visible (fixes the behind-a-tab
	 * mis-centering), then invalidateSize(). Falls back to an immediate mount where IntersectionObserver
	 * is unavailable.
	 */
	_observeAndMountMap(rawHTMLID, pInput)
	{
		const tmpContainer = (typeof document !== 'undefined') ? document.getElementById(`MAP-CONTAINER-FOR-${rawHTMLID}`) : null;
		if (!tmpContainer)
		{
			return;
		}
		// Keep an existing map ONLY if its container is still in the live DOM inside the current container.
		// A re-render (e.g. a tabular row add/delete calls pView.render(), which also regenerates this
		// field's DOM) swaps in a fresh empty container and detaches the old map — so a plain "map already
		// exists" guard would leave the map gone for good. Detect the stale map, tear it down, and re-mount.
		const tmpExisting = this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[rawHTMLID];
		if (tmpExisting && tmpExisting.map)
		{
			const tmpMapEl = (typeof tmpExisting.map.getContainer === 'function') ? tmpExisting.map.getContainer() : null;
			if (tmpMapEl && document.body.contains(tmpMapEl) && tmpContainer.contains(tmpMapEl))
			{
				return;
			}
			try
			{
 tmpExisting.map.remove();
			}
			catch (pError)
			{/* already detached */}
			delete this.pict.AppData.LeafletMapsData[rawHTMLID];
		}
		// A prior observer may still be watching the old (detached) container — retire it before re-observing.
		if (this._mapObservers[rawHTMLID])
		{
			this._mapObservers[rawHTMLID].disconnect();
			delete this._mapObservers[rawHTMLID];
		}
		const tmpMount = () =>
		{
			if (this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[rawHTMLID] && this.pict.AppData.LeafletMapsData[rawHTMLID].map)
			{
				return;
			}
			this.initializeLeafletMap(rawHTMLID, pInput);
			const tmpMapData = this.pict.AppData.LeafletMapsData[rawHTMLID];
			if (tmpMapData && tmpMapData.map)
			{
				setTimeout(() =>
				{
					try
					{
 tmpMapData.map.invalidateSize();
					}
					catch (pError)
					{/* container detached */}
				}, 60);
			}
		};
		if (typeof IntersectionObserver === 'undefined')
		{
			tmpMount();
			return;
		}
		const tmpObserver = new IntersectionObserver((pEntries) =>
		{
			for (let i = 0; i < pEntries.length; i++)
			{
				if (pEntries[i].isIntersecting)
				{
					tmpObserver.disconnect();
					delete this._mapObservers[rawHTMLID];
					tmpMount();
					return;
				}
			}
		});
		this._mapObservers[rawHTMLID] = tmpObserver;
		tmpObserver.observe(tmpContainer);

		// Fallback for environments where the observer callback may not fire in time (e.g. a headless PDF
		// render that never scrolls): if the container is already visible shortly after init and still
		// unmounted, mount eagerly. A container hidden behind a tab has offsetParent === null, so it stays
		// deferred to the observer.
		setTimeout(() =>
		{
			if (this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[rawHTMLID] && this.pict.AppData.LeafletMapsData[rawHTMLID].map)
			{
				return;
			}
			if (tmpContainer.offsetParent !== null)
			{
				if (this._mapObservers[rawHTMLID])
				{
					this._mapObservers[rawHTMLID].disconnect();
					delete this._mapObservers[rawHTMLID];
				}
				tmpMount();
			}
		}, 250);
	}

	/**
	 * Render the shared Leaflet view into this field's map container, seed the marker from the current
	 * lat/long, and wire click-to-place (unless the inputs are locked/read-only).
	 */
	initializeLeafletMap(rawHTMLID, pInput)
	{
		this.pict.views.GeolocationSelector.rawHTMLID = rawHTMLID;
		this.pict.views.GeolocationSelector.render('GeolocationSelector', this.getMapContainerHTMLID(rawHTMLID), pInput);

		const tmpMapData = this.pict.AppData.LeafletMapsData[rawHTMLID];
		if (!tmpMapData || !tmpMapData.map)
		{
			return;
		}
		const tmpMap = tmpMapData.map;

		// Seed from the (now marshaled) input values.
		const tmpLatVal = $(this.getLatitudeInputHTMLID(rawHTMLID)).val();
		const tmpLngVal = $(this.getLongitudeInputHTMLID(rawHTMLID)).val();
		if (this._inRange(tmpLatVal, tmpLngVal))
		{
			const tmpLat = Number(tmpLatVal);
			const tmpLng = Number(tmpLngVal);
			tmpMapData.marker = this.setLeafletMarker({ lat: tmpLat, lng: tmpLng }, tmpMap, null, rawHTMLID);
			tmpMapData.location = { Latitude: String(tmpLat), Longitude: String(tmpLng) };
			tmpMap.setView([ tmpLat, tmpLng ], Math.max(tmpMap.getZoom() || 13, 13));
		}

		// Read theme renders the inputs readonly/aria-readonly; keep the map non-interactive to match.
		const tmpLatInput = $(this.getLatitudeInputHTMLID(rawHTMLID));
		const tmpLocked = tmpLatInput.is(':disabled') || tmpLatInput.is('[readonly]') || tmpLatInput.attr('aria-readonly') === 'true';
		if (tmpLocked)
		{
			this.disableMap(tmpMap, tmpMapData.destinationAddress);
		}
		else
		{
			tmpMap.on('click', this.onMapSelectionMade, this);
		}
	}

	disableMap(pMap, pDestinationAddress)
	{
		pMap.dragging.disable();
		pMap.touchZoom.disable();
		pMap.doubleClickZoom.disable();
		pMap.scrollWheelZoom.disable();
		pMap.boxZoom.disable();
		pMap.keyboard.disable();
		// @ts-ignore
		if (pMap.tap) pMap.tap.disable();
		const tmpEl = document.getElementById(pDestinationAddress);
		if (tmpEl)
		{
			tmpEl.style.cursor = 'default';
		}
	}

	/**
	 * Create (draggable) or move a marker. Does NOT recenter the map — callers control the view so a drag
	 * doesn't yank the map out from under the user.
	 */
	setLeafletMarker(pLatLng, pMap, pMarker, pRawHTMLID)
	{
		if (pLatLng.lat == null || pLatLng.lng == null)
		{
			return pMarker;
		}
		const libLeaflet = window.L;
		let tmpMarker = pMarker;
		if (!tmpMarker)
		{
			tmpMarker = libLeaflet.marker(pLatLng, { draggable: true });
			tmpMarker.on('dragend', (pEvent) =>
			{
				const tmpLatLng = pEvent.target.getLatLng();
				// Marker is already where the user dropped it — commit the value without recentering.
				this._commitLatLong(pRawHTMLID, tmpLatLng.lat, tmpLatLng.lng);
			});
		}
		tmpMarker.setLatLng(pLatLng).addTo(pMap);
		return tmpMarker;
	}

	/** Map click handler: place the marker at the clicked point and commit (no recenter). */
	onMapSelectionMade(pEvent)
	{
		const tmpContainerID = (pEvent && pEvent.target && (typeof pEvent.target.getContainer === 'function')) ? pEvent.target.getContainer().id : '';
		const rawHTMLID = this.getMapIDFromContainerID(tmpContainerID);
		if (!rawHTMLID)
		{
			return;
		}
		const tmpMapData = this.pict.AppData.LeafletMapsData[rawHTMLID];
		if (tmpMapData && tmpMapData.map)
		{
			tmpMapData.marker = this.setLeafletMarker({ lat: pEvent.latlng.lat, lng: pEvent.latlng.lng }, tmpMapData.map, tmpMapData.marker, rawHTMLID);
		}
		this._commitLatLong(rawHTMLID, pEvent.latlng.lat, pEvent.latlng.lng);
	}

	/**
	 * Move the marker + recenter to a programmatically supplied point (typed coordinates, address pick, or
	 * GPS). Pure map operation — does not write the inputs (the caller commits separately), which avoids a
	 * commit/change loop.
	 */
	moveMarkerTo(rawHTMLID, pLatitude, pLongitude)
	{
		const tmpMapData = this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[rawHTMLID];
		if (!tmpMapData || !tmpMapData.map)
		{
			return;
		}
		const tmpLat = Number(pLatitude);
		const tmpLng = Number(pLongitude);
		if (!Number.isFinite(tmpLat) || !Number.isFinite(tmpLng))
		{
			return;
		}
		tmpMapData.marker = this.setLeafletMarker({ lat: tmpLat, lng: tmpLng }, tmpMapData.map, tmpMapData.marker, rawHTMLID);
		tmpMapData.location = { Latitude: String(tmpLat), Longitude: String(tmpLng) };
		tmpMapData.map.setView([ tmpLat, tmpLng ], Math.max(tmpMapData.map.getZoom() || 13, 13));
	}

	/**
	 * Write both lat/long inputs, repaint the readout, and marshal (single change on the latitude input;
	 * onDataChange handles the pair). The suppress flag prevents onDataChange from re-syncing the map.
	 */
	_commitLatLong(rawHTMLID, pLatitude, pLongitude)
	{
		const tmpLat = $(this.getLatitudeInputHTMLID(rawHTMLID));
		const tmpLong = $(this.getLongitudeInputHTMLID(rawHTMLID));
		if (tmpLat.length)
		{
			tmpLat.val(String(pLatitude));
		}
		if (tmpLong.length)
		{
			tmpLong.val(String(pLongitude));
		}
		this.refreshCombinedDisplay(rawHTMLID);
		const tmpMapData = this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[rawHTMLID];
		if (tmpMapData)
		{
			tmpMapData.location = { Latitude: String(pLatitude), Longitude: String(pLongitude) };
		}
		if (rawHTMLID === GEO_MODAL_KEY)
		{
			this._modalValue = { Latitude: String(pLatitude), Longitude: String(pLongitude) };
		}
		this._suppressMapSync[rawHTMLID] = true;
		if (tmpLat.length)
		{
			tmpLat.trigger('change');
		}
		this._suppressMapSync[rawHTMLID] = false;
	}

	// --- Use My Location (device GPS) ----------------------------------------------------------------

	onUseMyLocation(rawHTMLID)
	{
		if (!navigator.geolocation)
		{
			this._toast('Could not fetch user location', 'is-error');
			return;
		}
		navigator.geolocation.getCurrentPosition(
			(pPosition) => this.setLocation(rawHTMLID, pPosition),
			() =>
			{
				this.pict.log.info('GeolocationSelector', 'onUseMyLocation', 'getCurrentPosition failed');
				this._toast('Could not fetch user location', 'is-error');
			});
	}

	setLocation(rawHTMLID, pPosition)
	{
		const tmpLat = pPosition.coords.latitude;
		const tmpLng = pPosition.coords.longitude;
		this._commitLatLong(rawHTMLID, tmpLat, tmpLng);
		this.moveMarkerTo(rawHTMLID, tmpLat, tmpLng);
	}

	// --- Address geocoding (Nominatim / OpenStreetMap) -----------------------------------------------

	/**
	 * Geocode the field's address box via public Nominatim — same provider as the tiles, no API key. One
	 * request per explicit Search (no as-you-type), keeping within OSM fair-use. Results render as clickable
	 * rows; picking one (chooseAddressResult) drops the pin.
	 * @param {string} rawHTMLID
	 */
	async searchAddress(rawHTMLID)
	{
		const tmpInputEl = document.getElementById(this.getAddressInputID(rawHTMLID));
		const tmpQuery = this._domValue(tmpInputEl).trim();
		if (!tmpQuery)
		{
			return;
		}
		this._renderAddressStatus(rawHTMLID, 'Searching…', false);
		try
		{
			const tmpURL = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(tmpQuery)}`;
			const tmpResponse = await fetch(tmpURL, { headers: { 'Accept': 'application/json' } });
			if (!tmpResponse.ok)
			{
				throw new Error(`the geocoder returned ${tmpResponse.status}`);
			}
			const tmpRows = await tmpResponse.json();
			this._addressResultsByID[rawHTMLID] = (Array.isArray(tmpRows) ? tmpRows : [])
				.map((pRow) => ({ DisplayName: String(pRow.display_name || 'result'), Latitude: Number(pRow.lat), Longitude: Number(pRow.lon) }))
				.filter((pRow) => Number.isFinite(pRow.Latitude) && Number.isFinite(pRow.Longitude))
				.slice(0, 5);
			this._renderAddressResults(rawHTMLID);
		}
		catch (pError)
		{
			this._addressResultsByID[rawHTMLID] = [];
			this._renderAddressStatus(rawHTMLID, `Address search failed: ${pError.message || pError}`, true);
		}
	}

	_clearAddressResultsDOM(rawHTMLID)
	{
		const tmpEl = document.getElementById(this.getAddressResultsID(rawHTMLID));
		if (tmpEl)
		{
			while (tmpEl.firstChild)
			{
				tmpEl.removeChild(tmpEl.firstChild);
			}
		}
		return tmpEl;
	}

	/** Render clickable result rows (createElement/append — no HTML-string injection). */
	_renderAddressResults(rawHTMLID)
	{
		const tmpEl = this._clearAddressResultsDOM(rawHTMLID);
		if (!tmpEl)
		{
			return;
		}
		const tmpResults = this._addressResultsByID[rawHTMLID] || [];
		if (tmpResults.length === 0)
		{
			this._renderAddressStatus(rawHTMLID, 'No matches found for that address.', false);
			return;
		}
		for (let i = 0; i < tmpResults.length; i++)
		{
			const tmpRow = document.createElement('button');
			tmpRow.type = 'button';
			tmpRow.className = 'button is-small is-fullwidth has-text-left mb-1';
			tmpRow.textContent = tmpResults[i].DisplayName;
			// eslint-disable-next-line no-loop-func
			tmpRow.addEventListener('click', ((pIndex) => () => this.chooseAddressResult(rawHTMLID, pIndex))(i));
			tmpEl.appendChild(tmpRow);
		}
	}

	_renderAddressStatus(rawHTMLID, pText, pIsError)
	{
		const tmpEl = this._clearAddressResultsDOM(rawHTMLID);
		if (!tmpEl)
		{
			return;
		}
		const tmpStatus = document.createElement('p');
		tmpStatus.className = pIsError ? 'help is-danger' : 'help';
		tmpStatus.textContent = pText;
		tmpEl.appendChild(tmpStatus);
	}

	chooseAddressResult(rawHTMLID, pIndex)
	{
		const tmpRow = (this._addressResultsByID[rawHTMLID] || [])[Number(pIndex)];
		if (!tmpRow)
		{
			return;
		}
		this._addressResultsByID[rawHTMLID] = [];
		this._clearAddressResultsDOM(rawHTMLID);
		// A full-size row's search is keyed by `<rawHTMLID>-<rowIndex>`; commit it to that row rather
		// than to a same-named single-record field.
		const tmpRowContext = this._rowContexts[rawHTMLID];
		if (tmpRowContext)
		{
			this._commitTabularRow(tmpRowContext.viewHash, tmpRowContext.groupIndex, tmpRowContext.inputIndex,
				tmpRowContext.rowIndex, tmpRowContext.rawHTMLID, tmpRow.Latitude, tmpRow.Longitude);
			return;
		}
		this._commitLatLong(rawHTMLID, tmpRow.Latitude, tmpRow.Longitude);
		this.moveMarkerTo(rawHTMLID, Number(tmpRow.Latitude), Number(tmpRow.Longitude));
	}

	// --- Clear / reset -------------------------------------------------------------------------------

	clearValue(rawHTMLID)
	{
		const tmpLat = $(this.getLatitudeInputHTMLID(rawHTMLID));
		const tmpLong = $(this.getLongitudeInputHTMLID(rawHTMLID));
		if (tmpLat.length)
		{
			tmpLat.val('');
		}
		if (tmpLong.length)
		{
			tmpLong.val('');
		}
		this.refreshCombinedDisplay(rawHTMLID);
		const tmpMapData = this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[rawHTMLID];
		if (tmpMapData)
		{
			tmpMapData.location = { Latitude: '', Longitude: '' };
			if (tmpMapData.marker && tmpMapData.map)
			{
				try
				{
 tmpMapData.map.removeLayer(tmpMapData.marker);
				}
				catch (pError)
				{/* already removed */}
				tmpMapData.marker = null;
			}
		}
		this._addressResultsByID[rawHTMLID] = [];
		this._clearAddressResultsDOM(rawHTMLID);
		this._suppressMapSync[rawHTMLID] = true;
		if (tmpLat.length)
		{
			tmpLat.trigger('change');
		}
		this._suppressMapSync[rawHTMLID] = false;
	}

	// --- Tabular -------------------------------------------------------------------------------------

	/** Repaint a tabular cell's readout (both presentations share the id) from a value. */
	_paintTabularDisplay(rawHTMLID, pRowIndex, pSeed)
	{
		if (!document.querySelector(this.getTabularDisplayID(rawHTMLID, pRowIndex)))
		{
			return;
		}
		this.pict.ContentAssignment.assignContent(this.getTabularDisplayID(rawHTMLID, pRowIndex),
			this.formatLocationDisplay(pSeed.Latitude, pSeed.Longitude));
	}

	/**
	 * Mirror a row's value into the full-size presentation's visible coordinate editors. No-op in the
	 * compact presentation, which has no editors (the value is edited in the modal instead).
	 */
	_syncRowEditors(rawHTMLID, pRowIndex, pSeed)
	{
		const tmpLatEl = document.querySelector(this.getRowEditorLatitudeID(rawHTMLID, pRowIndex));
		const tmpLngEl = document.querySelector(this.getRowEditorLongitudeID(rawHTMLID, pRowIndex));
		if (!tmpLatEl && !tmpLngEl)
		{
			return;
		}
		this._setDomValue(tmpLatEl, pSeed.Latitude);
		this._setDomValue(tmpLngEl, pSeed.Longitude);
	}

	/** Repaint a tabular cell's derived display + editors from its two hidden inputs. */
	_refreshTabularDisplay(rawHTMLID, pRowIndex)
	{
		const tmpLatEl = document.querySelector(this.getTabularLatitudeInputID(rawHTMLID, pRowIndex));
		const tmpLngEl = document.querySelector(this.getTabularLongitudeInputID(rawHTMLID, pRowIndex));
		const tmpSeed = { Latitude: this._domValue(tmpLatEl), Longitude: this._domValue(tmpLngEl) };
		this._paintTabularDisplay(rawHTMLID, pRowIndex, tmpSeed);
		this._syncRowEditors(rawHTMLID, pRowIndex, tmpSeed);
	}

	/** Write a row's two hidden inputs and repaint its display (does not marshal — caller does). */
	setTabularLatLong(rawHTMLID, pRowIndex, pLatitude, pLongitude)
	{
		const tmpLatEl = document.querySelector(this.getTabularLatitudeInputID(rawHTMLID, pRowIndex));
		const tmpLngEl = document.querySelector(this.getTabularLongitudeInputID(rawHTMLID, pRowIndex));
		this._setDomValue(tmpLatEl, pLatitude);
		this._setDomValue(tmpLngEl, pLongitude);
		this._refreshTabularDisplay(rawHTMLID, pRowIndex);
	}

	/**
	 * Commit a row's value from any of its controls: hidden inputs + derived display + editors, the
	 * inline record-set marker, then marshal the row. Every row-level action funnels through here.
	 */
	_commitTabularRow(pViewHash, pGroupIndex, pInputIndex, pRowIndex, pRawHTMLID, pLatitude, pLongitude)
	{
		this.setTabularLatLong(pRawHTMLID, pRowIndex, pLatitude, pLongitude);
		this._syncRecordSetRowMarker(`RS-${pRawHTMLID}-${pRowIndex}`, { Latitude: pLatitude, Longitude: pLongitude });
		if (this.pict.views[pViewHash])
		{
			this.pict.views[pViewHash].dataChangedTabular(pGroupIndex, pInputIndex, pRowIndex);
		}
	}

	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this._injectCSS();
		const tmpSeed = this._coerceLatLong(pValue);
		this._paintTabularDisplay(pInput.Macro.RawHTMLID, pRowIndex, tmpSeed);
		this._syncRowEditors(pInput.Macro.RawHTMLID, pRowIndex, tmpSeed);
		// Record Sets render each record stacked (not a compact table), so the map variant gets an inline
		// per-record map. The compact Tabular table skips this and keeps its map in the Edit modal.
		if (pGroup && pGroup.Layout === 'RecordSet')
		{
			this._ensureRecordSetRowMap(pView, pGroup, pInput, pRowIndex, tmpSeed);
			this._refreshRecordSetRowStaticImage(pInput, pRowIndex, tmpSeed);
		}
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/** The readout and editors are derived state, so they must be repainted to the row's value after a reorder. */
	onTabularRowReorderRepaintTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		const tmpSeed = this._coerceLatLong(pValue);
		this._paintTabularDisplay(pInput.Macro.RawHTMLID, pRowIndex, tmpSeed);
		this._syncRowEditors(pInput.Macro.RawHTMLID, pRowIndex, tmpSeed);
		if (pGroup && pGroup.Layout === 'RecordSet')
		{
			this._ensureRecordSetRowMap(pView, pGroup, pInput, pRowIndex, tmpSeed);
			this._refreshRecordSetRowStaticImage(pInput, pRowIndex, tmpSeed);
		}
		// (base method is a no-op; not chaining super avoids a stale shipped-.d.ts type error)
		return true;
	}

	// --- Record Set inline per-record map (map variant only) -----------------------------------------

	/**
	 * Reveal + (re)mount the inline map for one record-set record. Visibility-gated (defers to an
	 * IntersectionObserver when the record is behind an inactive tab) and stale-aware (a record-set
	 * re-render on row add/delete regenerates the host, so the detached map is torn down and re-mounted).
	 * No-op for the no-map variant (no host element).
	 * @param {any} pView @param {any} pGroup @param {any} pInput @param {number} pRowIndex @param {any} pSeed
	 */
	_ensureRecordSetRowMap(pView, pGroup, pInput, pRowIndex, pSeed)
	{
		const rawHTMLID = pInput.Macro.RawHTMLID;
		const tmpHost = document.getElementById(`GEO-RS-MAP-FOR-${rawHTMLID}-${pRowIndex}`);
		if (!tmpHost)
		{
			return;
		}
		tmpHost.style.display = 'block';
		const tmpLocked = tmpHost.getAttribute('data-geo-locked') === 'true';
		const tmpKey = `RS-${rawHTMLID}-${pRowIndex}`;
		const tmpContext = { view: pView, groupIndex: pGroup.GroupIndex, inputIndex: pInput.PictForm.InputIndex, rowIndex: pRowIndex, rawHTMLID };

		const tmpExisting = this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[tmpKey];
		if (tmpExisting && tmpExisting.map)
		{
			const tmpEl = (typeof tmpExisting.map.getContainer === 'function') ? tmpExisting.map.getContainer() : null;
			if (tmpEl && tmpEl === tmpHost && document.body.contains(tmpEl))
			{
				tmpExisting.recordSet = tmpContext;
				this._syncRecordSetRowMarker(tmpKey, pSeed);
				return;
			}
			try
			{
 tmpExisting.map.remove();
			}
			catch (pError)
			{/* already detached */}
			delete this.pict.AppData.LeafletMapsData[tmpKey];
		}
		if (this._mapObservers[tmpKey])
		{
			this._mapObservers[tmpKey].disconnect();
			delete this._mapObservers[tmpKey];
		}

		const tmpMount = () =>
		{
			if (this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[tmpKey] && this.pict.AppData.LeafletMapsData[tmpKey].map)
			{
				return;
			}
			this._mountRecordSetRowMap(tmpKey, tmpHost, tmpContext, pSeed, tmpLocked);
		};
		if ((typeof IntersectionObserver === 'undefined') || (tmpHost.offsetParent !== null))
		{
			tmpMount();
			return;
		}
		const tmpObserver = new IntersectionObserver((pEntries) =>
		{
			for (let i = 0; i < pEntries.length; i++)
			{
				if (pEntries[i].isIntersecting)
				{
					tmpObserver.disconnect();
					delete this._mapObservers[tmpKey];
					tmpMount();
					return;
				}
			}
		});
		this._mapObservers[tmpKey] = tmpObserver;
		tmpObserver.observe(tmpHost);
	}

	_mountRecordSetRowMap(pKey, pHost, pContext, pSeed, pLocked)
	{
		const libLeaflet = window.L;
		if (!libLeaflet || !pHost)
		{
			return;
		}
		const tmpSeed = this._coerceLatLong(pSeed);
		const tmpHasFix = this._inRange(tmpSeed.Latitude, tmpSeed.Longitude);
		const tmpLat = tmpHasFix ? Number(tmpSeed.Latitude) : GEO_DEFAULT_CENTER.Latitude;
		const tmpLng = tmpHasFix ? Number(tmpSeed.Longitude) : GEO_DEFAULT_CENTER.Longitude;

		const tmpMap = libLeaflet.map(pHost).setView([ tmpLat, tmpLng ], tmpHasFix ? 13 : 4);
		libLeaflet.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OpenStreetMap contributors' }).addTo(tmpMap);

		this.pict.AppData.LeafletMapsData = this.pict.AppData.LeafletMapsData || {};
		this.pict.AppData.LeafletMapsData[pKey] = {
			map: tmpMap,
			RawHTMLID: pKey,
			destinationAddress: pHost.id,
			hideMap: false,
			marker: null,
			location: { Latitude: '', Longitude: '' },
			locked: pLocked,
			recordSet: pContext,
		};
		if (tmpHasFix)
		{
			this.pict.AppData.LeafletMapsData[pKey].marker = this._recordSetMarker(pKey, tmpLat, tmpLng);
			this.pict.AppData.LeafletMapsData[pKey].location = { Latitude: String(tmpLat), Longitude: String(tmpLng) };
		}
		if (pLocked)
		{
			this.disableMap(tmpMap, pHost.id);
		}
		else
		{
			tmpMap.on('click', (pEvent) => this._onRecordSetRowMapClick(pKey, pEvent.latlng.lat, pEvent.latlng.lng));
		}
		setTimeout(() =>
		{
			try
			{
 tmpMap.invalidateSize();
			}
			catch (pError)
			{/* host detached */}
		}, 60);
	}

	/** Create (draggable) or move the record's marker. */
	_recordSetMarker(pKey, pLatitude, pLongitude)
	{
		const tmpMapData = this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[pKey];
		if (!tmpMapData || !tmpMapData.map)
		{
			return tmpMapData ? tmpMapData.marker : null;
		}
		const libLeaflet = window.L;
		const tmpLatLng = { lat: Number(pLatitude), lng: Number(pLongitude) };
		let tmpMarker = tmpMapData.marker;
		if (!tmpMarker)
		{
			tmpMarker = libLeaflet.marker(tmpLatLng, { draggable: !tmpMapData.locked });
			if (!tmpMapData.locked)
			{
				tmpMarker.on('dragend', (pEvent) =>
				{
					const tmpDrag = pEvent.target.getLatLng();
					this._commitRecordSetRow(pKey, tmpDrag.lat, tmpDrag.lng);
				});
			}
		}
		tmpMarker.setLatLng(tmpLatLng).addTo(tmpMapData.map);
		return tmpMarker;
	}

	_onRecordSetRowMapClick(pKey, pLatitude, pLongitude)
	{
		const tmpMapData = this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[pKey];
		if (!tmpMapData)
		{
			return;
		}
		tmpMapData.marker = this._recordSetMarker(pKey, pLatitude, pLongitude);
		this._commitRecordSetRow(pKey, pLatitude, pLongitude);
	}

	/** Write a record's value from its map gesture: hidden inputs + compact display, then marshal the row. */
	_commitRecordSetRow(pKey, pLatitude, pLongitude)
	{
		const tmpMapData = this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[pKey];
		if (!tmpMapData || !tmpMapData.recordSet)
		{
			return;
		}
		const tmpContext = tmpMapData.recordSet;
		tmpMapData.location = { Latitude: String(pLatitude), Longitude: String(pLongitude) };
		this.setTabularLatLong(tmpContext.rawHTMLID, tmpContext.rowIndex, pLatitude, pLongitude);
		if (tmpContext.view && (typeof tmpContext.view.dataChangedTabular === 'function'))
		{
			tmpContext.view.dataChangedTabular(tmpContext.groupIndex, tmpContext.inputIndex, tmpContext.rowIndex);
		}
	}

	/** Refresh a healthy record map's marker to the marshaled row value (marker follows external edits). */
	_syncRecordSetRowMarker(pKey, pSeed)
	{
		const tmpMapData = this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[pKey];
		if (!tmpMapData || !tmpMapData.map)
		{
			return;
		}
		const tmpSeed = this._coerceLatLong(pSeed);
		if (this._inRange(tmpSeed.Latitude, tmpSeed.Longitude))
		{
			const tmpLat = Number(tmpSeed.Latitude);
			const tmpLng = Number(tmpSeed.Longitude);
			tmpMapData.marker = this._recordSetMarker(pKey, tmpLat, tmpLng);
			tmpMapData.location = { Latitude: String(tmpLat), Longitude: String(tmpLng) };
			// Recenter: a non-gesture set (Get-My-Location, Edit modal) may land off-screen, so bring it
			// into view. Map drag/click never routes here, so a drag is never yanked back to center.
			tmpMapData.map.setView([ tmpLat, tmpLng ], Math.max(tmpMapData.map.getZoom() || 13, 13));
		}
		else if (tmpMapData.marker)
		{
			try
			{
 tmpMapData.map.removeLayer(tmpMapData.marker);
			}
			catch (pError)
			{/* already gone */}
			tmpMapData.marker = null;
			tmpMapData.location = { Latitude: '', Longitude: '' };
		}
	}

	onUseMyLocationTabular(pViewHash, pGroupIndex, pInputIndex, pRowIndex, pRawHTMLID)
	{
		if (!navigator.geolocation)
		{
			this._toast('Could not fetch user location', 'is-error');
			return;
		}
		navigator.geolocation.getCurrentPosition(
			(pPosition) => this._commitTabularRow(pViewHash, pGroupIndex, pInputIndex, pRowIndex, pRawHTMLID,
				pPosition.coords.latitude, pPosition.coords.longitude),
			() => this._toast('Could not fetch user location', 'is-error'));
	}

	clearValueTabular(pViewHash, pGroupIndex, pInputIndex, pRowIndex, pRawHTMLID)
	{
		const tmpKey = this.getRowKey(pRawHTMLID, pRowIndex);
		this._addressResultsByID[tmpKey] = [];
		this._clearAddressResultsDOM(tmpKey);
		this._commitTabularRow(pViewHash, pGroupIndex, pInputIndex, pRowIndex, pRawHTMLID, '', '');
	}

	/**
	 * A typed edit in the full-size row presentation's coordinate editors. Those inputs are unbound,
	 * so the value is pushed into the row's hidden informary inputs and marshaled from here.
	 */
	onRowCoordinateEdit(pViewHash, pGroupIndex, pInputIndex, pRowIndex, pRawHTMLID)
	{
		const tmpLat = this._domValue(document.querySelector(this.getRowEditorLatitudeID(pRawHTMLID, pRowIndex)));
		const tmpLng = this._domValue(document.querySelector(this.getRowEditorLongitudeID(pRawHTMLID, pRowIndex)));
		this._commitTabularRow(pViewHash, pGroupIndex, pInputIndex, pRowIndex, pRawHTMLID, tmpLat, tmpLng);
	}

	/**
	 * Address search from the full-size row presentation. The search DOM is keyed by instance id, so
	 * the row's identity is stashed first — chooseAddressResult reads it back to commit the pick to
	 * the right row.
	 */
	searchAddressTabular(pViewHash, pGroupIndex, pInputIndex, pRowIndex, pRawHTMLID)
	{
		const tmpKey = this.getRowKey(pRawHTMLID, pRowIndex);
		this._rowContexts[tmpKey] = { viewHash: pViewHash, groupIndex: pGroupIndex, inputIndex: pInputIndex, rowIndex: pRowIndex, rawHTMLID: pRawHTMLID };
		return this.searchAddress(tmpKey);
	}

	openEditModalTabular(pViewHash, pGroupIndex, pInputIndex, pRowIndex, pRawHTMLID)
	{
		const tmpView = this.pict.views[pViewHash];
		const tmpInput = (tmpView && typeof tmpView.getTabularRecordInput === 'function') ? tmpView.getTabularRecordInput(pGroupIndex, pInputIndex) : null;
		const tmpHasMap = !!(tmpInput && tmpInput.PictForm && tmpInput.PictForm.InputType === 'GeolocationSelectorWithMap');
		const tmpLatEl = document.querySelector(this.getTabularLatitudeInputID(pRawHTMLID, pRowIndex));
		const tmpLngEl = document.querySelector(this.getTabularLongitudeInputID(pRawHTMLID, pRowIndex));
		const tmpCurrent = { Latitude: this._domValue(tmpLatEl), Longitude: this._domValue(tmpLngEl) };
		this._openLocationModal(tmpHasMap, tmpCurrent, (pResult) =>
			this._commitTabularRow(pViewHash, pGroupIndex, pInputIndex, pRowIndex, pRawHTMLID, pResult.Latitude, pResult.Longitude));
	}

	/**
	 * Open the edit-location modal for a single-record field — the compact presentation's Edit button,
	 * where the picker lives in the modal instead of inline. Saving commits through the same path a
	 * map gesture uses, so the inline map (map variant) follows.
	 * @param {string} rawHTMLID
	 * @param {boolean} pHasMap
	 */
	openEditModal(rawHTMLID, pHasMap)
	{
		const tmpCurrent = {
			Latitude: $(this.getLatitudeInputHTMLID(rawHTMLID)).val(),
			Longitude: $(this.getLongitudeInputHTMLID(rawHTMLID)).val(),
		};
		this._openLocationModal(!!pHasMap, tmpCurrent, (pResult) =>
		{
			// An emptied modal is a clear — clearValue also drops the marker, which a bare commit
			// would leave stranded at the old point.
			if (!this._inRange(pResult.Latitude, pResult.Longitude))
			{
				this.clearValue(rawHTMLID);
				return;
			}
			this._commitLatLong(rawHTMLID, pResult.Latitude, pResult.Longitude);
			this.moveMarkerTo(rawHTMLID, Number(pResult.Latitude), Number(pResult.Longitude));
		});
	}

	// --- Edit-location modal (hosts the full picker; used by tabular Edit) ----------------------------

	/**
	 * The modal reuses the field-level methods keyed by GEO_MODAL_KEY: its lat/long inputs are
	 * LATITUDE-FOR-GEO-MODAL / LONGITUDE-FOR-GEO-MODAL, its search box GEO-ADDRESS-INPUT-GEO-MODAL, and its
	 * map is a self-contained Leaflet instance in LeafletMapsData[GEO_MODAL_KEY].
	 */
	_buildModalContentHTML(pHasMap)
	{
		const tmpKey = GEO_MODAL_KEY;
		let tmpHTML = '' +
			'<div class="field has-addons">' +
			'<div class="control is-expanded"><input id="' + this.getAddressInputID(tmpKey) + '" class="input" type="text" placeholder="Search an address or place…" autocomplete="off" ' +
			'onkeydown="if(event.key===\'Enter\'){event.preventDefault();_Pict.providers.GeolocationSelector.searchAddress(\'' + tmpKey + '\');}" /></div>' +
			'<div class="control"><button type="button" class="button is-soft" onclick="_Pict.providers.GeolocationSelector.searchAddress(\'' + tmpKey + '\')"><span class="icon"><i class="fas fa-search"></i></span><span class="is-hidden-mobile">Search</span></button></div>' +
			'</div>' +
			'<div id="' + this.getAddressResultsID(tmpKey) + '"></div>' +
			// Plain columns (no is-mobile) so the coordinate pair stacks into full-width rows on a phone
			// rather than truncating long decimals side by side.
			'<div class="columns mt-1">' +
			'<div class="column"><label class="label is-small">Latitude</label>' +
			'<input id="LATITUDE-FOR-' + tmpKey + '" class="input" type="number" step="any" onchange="_Pict.providers.GeolocationSelector.onModalCoordEdit()" /></div>' +
			'<div class="column"><label class="label is-small">Longitude</label>' +
			'<input id="LONGITUDE-FOR-' + tmpKey + '" class="input" type="number" step="any" onchange="_Pict.providers.GeolocationSelector.onModalCoordEdit()" /></div>' +
			'</div>' +
			// geo-action-buttons splits the pair evenly across the row on a phone, matching the
			// inline control's action row.
			'<div class="buttons are-small mt-1 geo-action-buttons">' +
			'<button type="button" class="button is-link" onclick="_Pict.providers.GeolocationSelector.onUseMyLocation(\'' + tmpKey + '\')"><span class="icon"><i class="fa fa-location-crosshairs"></i></span><span>Use My Location</span></button>' +
			'<button type="button" class="button is-link is-soft" onclick="_Pict.providers.GeolocationSelector.clearValue(\'' + tmpKey + '\')"><span class="icon"><i class="fa fa-eraser"></i></span><span>Clear</span></button>' +
			'</div>';
		if (pHasMap)
		{
			tmpHTML += '<div id="MAP-CONTAINER-FOR-' + tmpKey + '" class="mt-2" style="min-height: 300px; z-index: 1;"></div>';
		}
		return tmpHTML;
	}

	/** Typed edit inside the modal: follow it on the modal map + capture the working value. */
	onModalCoordEdit()
	{
		const tmpKey = GEO_MODAL_KEY;
		const tmpLat = (this._domValue(document.getElementById(`LATITUDE-FOR-${tmpKey}`)));
		const tmpLng = (this._domValue(document.getElementById(`LONGITUDE-FOR-${tmpKey}`)));
		this._modalValue = { Latitude: tmpLat, Longitude: tmpLng };
		if (this._inRange(tmpLat, tmpLng))
		{
			this.moveMarkerTo(tmpKey, Number(tmpLat), Number(tmpLng));
		}
	}

	_mountModalMap(pCurrent)
	{
		const tmpKey = GEO_MODAL_KEY;
		const libLeaflet = window.L;
		const tmpHost = document.getElementById(`MAP-CONTAINER-FOR-${tmpKey}`);
		if (!libLeaflet || !tmpHost)
		{
			return;
		}
		const tmpSeed = this._coerceLatLong(pCurrent);
		const tmpHasFix = this._inRange(tmpSeed.Latitude, tmpSeed.Longitude);
		const tmpLat = tmpHasFix ? Number(tmpSeed.Latitude) : GEO_DEFAULT_CENTER.Latitude;
		const tmpLng = tmpHasFix ? Number(tmpSeed.Longitude) : GEO_DEFAULT_CENTER.Longitude;

		const tmpMap = libLeaflet.map(tmpHost).setView([ tmpLat, tmpLng ], tmpHasFix ? 13 : 4);
		libLeaflet.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom: 19, attribution: '&copy; OpenStreetMap contributors' }).addTo(tmpMap);

		this.pict.AppData.LeafletMapsData = this.pict.AppData.LeafletMapsData || {};
		this.pict.AppData.LeafletMapsData[tmpKey] = {
			map: tmpMap,
			RawHTMLID: tmpKey,
			destinationAddress: `MAP-CONTAINER-FOR-${tmpKey}`,
			hideMap: false,
			marker: null,
			location: { Latitude: '', Longitude: '' },
		};
		if (tmpHasFix)
		{
			this.pict.AppData.LeafletMapsData[tmpKey].marker = this.setLeafletMarker({ lat: tmpLat, lng: tmpLng }, tmpMap, null, tmpKey);
			this.pict.AppData.LeafletMapsData[tmpKey].location = { Latitude: String(tmpLat), Longitude: String(tmpLng) };
		}
		tmpMap.on('click', this.onMapSelectionMade, this);
		setTimeout(() =>
		{
			try
			{
 tmpMap.invalidateSize();
			}
			catch (pError)
			{/* host detached */}
		}, 80);
	}

	_teardownModalMap()
	{
		const tmpKey = GEO_MODAL_KEY;
		const tmpMapData = this.pict.AppData.LeafletMapsData && this.pict.AppData.LeafletMapsData[tmpKey];
		if (tmpMapData && tmpMapData.map)
		{
			try
			{
 tmpMapData.map.remove();
			}
			catch (pError)
			{/* already removed */}
		}
		if (this.pict.AppData.LeafletMapsData)
		{
			delete this.pict.AppData.LeafletMapsData[tmpKey];
		}
		delete this._addressResultsByID[tmpKey];
	}

	/**
	 * Open the shared edit-location modal, prefilled to pCurrent, and invoke pOnSave({Latitude, Longitude})
	 * when the user confirms. The working value is captured live (this._modalValue), so Save is robust to
	 * modal-DOM teardown timing.
	 */
	_openLocationModal(pHasMap, pCurrent, pOnSave)
	{
		const tmpKey = GEO_MODAL_KEY;
		const tmpModal = this.pict.views['Pict-Section-Modal'];
		if (!tmpModal || (typeof tmpModal.show !== 'function'))
		{
			this._toast('Location editor is unavailable', 'is-error');
			return;
		}
		this._addressResultsByID[tmpKey] = [];
		this._modalValue = { Latitude: pCurrent ? pCurrent.Latitude : '', Longitude: pCurrent ? pCurrent.Longitude : '' };

		tmpModal.show(
		{
			title: 'Edit Location',
			content: this._buildModalContentHTML(pHasMap),
			closeable: true,
			buttons:
			[
				{ Hash: 'cancel', Label: 'Cancel' },
				{ Hash: 'save', Label: 'Save', Style: 'primary' },
			],
			onOpen: (pDialog) =>
			{
				// Scopes the phone-only full-screen rules to this dialog (the shared modal has no
				// full-screen option of its own).
				if (pDialog && pDialog.classList)
				{
					pDialog.classList.add('geolocation-edit-modal');
				}
				const tmpLatEl = document.getElementById(`LATITUDE-FOR-${tmpKey}`);
				const tmpLngEl = document.getElementById(`LONGITUDE-FOR-${tmpKey}`);
				if (pCurrent && pCurrent.Latitude !== '' && pCurrent.Latitude != null)
				{
					this._setDomValue(tmpLatEl, pCurrent.Latitude);
				}
				if (pCurrent && pCurrent.Longitude !== '' && pCurrent.Longitude != null)
				{
					this._setDomValue(tmpLngEl, pCurrent.Longitude);
				}
				if (pHasMap)
				{
					this._mountModalMap(pCurrent);
				}
				if (tmpLatEl)
				{
					tmpLatEl.focus();
				}
			},
			onClose: () => this._teardownModalMap(),
		}).then((pHash) =>
		{
			if (pHash !== 'save')
			{
				return;
			}
			pOnSave({ Latitude: this._modalValue.Latitude, Longitude: this._modalValue.Longitude });
		});
	}
}

module.exports = CustomInputHandler;
